import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Printer,
  Plus,
  Minus,
  ListRestart,
  Search,
  Download,
  ArrowUp,
  Clock,
  User,
  Phone,
  Settings,
  X,
  Save,
  Edit2,
  CreditCard,
} from "lucide-react";
import Navbar from "../components/Home/Navbar";
import Footer from "../components/Home/Footer";

// Firebase Imports
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  updateDoc,
} from "firebase/firestore";

const Dashboard = () => {
  // --- STATE MANAGEMENT ---
  const [activeTab, setActiveTab] = useState("billing");
  const [reprintData, setReprintData] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [history, setHistory] = useState([]);
  const [sevalu, setSevalu] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [devoteeName, setDevoteeName] = useState("");
  const [devoteeMobile, setDevoteeMobile] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [mobileError, setMobileError] = useState("");

  const [showSevaModal, setShowSevaModal] = useState(false);
  const [newSeva, setNewSeva] = useState({ peru: "", dhara: "" });
  const [editingSevaId, setEditingSevaId] = useState(null);

  const scrollRef = useRef(null);

  // --- FILTER STATES ---
  const [filterType, setFilterType] = useState("date");
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [filterMonth, setFilterMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [filterYear, setFilterYear] = useState(
    new Date().getFullYear().toString(),
  );

  // --- DATA FETCHING ---
  useEffect(() => {
    const sevaQuery = query(collection(db, "sevalu"), orderBy("peru", "asc"));
    const unsubSeva = onSnapshot(sevaQuery, (snapshot) => {
      setSevalu(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    const transQuery = query(
      collection(db, "transactions"),
      orderBy("createdAt", "desc"),
    );
    const unsubTrans = onSnapshot(transQuery, (snapshot) => {
      setHistory(
        snapshot.docs.map((doc) => {
          const data = doc.data();
          const dateObj = data.createdAt?.toDate() || new Date();
          return {
            id: doc.id,
            ...data,
            dateString: dateObj.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            timeString: dateObj.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            rawDate: dateObj.toISOString().split("T")[0],
          };
        }),
      );
    });

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      unsubSeva();
      unsubTrans();
      clearInterval(timer);
    };
  }, []);

  // --- VALIDATION ---
  useEffect(() => {
    if (devoteeMobile && devoteeMobile.length !== 10) {
      setMobileError("Enter a valid 10-digit number");
    } else {
      setMobileError("");
    }
  }, [devoteeMobile]);

  // --- CORE LOGIC: DYNAMIC SR NUMBER CALCULATION ---
  const dailyIdMap = useMemo(() => {
    const map = {};
    const grouped = {};

    history.forEach((t) => {
      if (!grouped[t.rawDate]) grouped[t.rawDate] = [];
      grouped[t.rawDate].push(t);
    });

    Object.values(grouped).forEach((dayList) => {
      const totalInDay = dayList.length;
      dayList.forEach((t, index) => {
        map[t.id] = (totalInDay - index).toString();
      });
    });

    return map;
  }, [history]);

  // --- LOGIC FUNCTIONS ---
  const handleSaveSeva = async (e) => {
    e.preventDefault();
    if (!newSeva.peru || !newSeva.dhara) return;
    try {
      if (editingSevaId) {
        await updateDoc(doc(db, "sevalu", editingSevaId), {
          peru: newSeva.peru,
          dhara: Number(newSeva.dhara),
        });
      } else {
        await addDoc(collection(db, "sevalu"), {
          peru: newSeva.peru,
          dhara: Number(newSeva.dhara),
          createdAt: serverTimestamp(),
        });
      }
      setNewSeva({ peru: "", dhara: "" });
      setEditingSevaId(null);
      setShowSevaModal(false);
    } catch (err) {
      console.error("Error saving seva");
    }
  };

  const startEditSeva = (item) => {
    setEditingSevaId(item.id);
    setNewSeva({ peru: item.peru, dhara: item.dhara });
  };

  const [pendingId, setPendingId] = useState(null);

  const handlePrint = async () => {
    const total = calculateTotal();
    if (total === 0 || mobileError) return;

    const todayStr = new Date().toISOString().split("T")[0];
    const dailyCount = history.filter((t) => t.rawDate === todayStr).length + 1;
    const nextId = dailyCount.toString();

    setPendingId(nextId);

    const transactionData = {
      createdAt: serverTimestamp(),
      transactionId: nextId,
      items: selectedItems.map((i) => ({
        peru: i.peru,
        qty: quantities[i.id],
        dhara: i.dhara,
      })),
      total,
      devoteeName,
      devoteeMobile,
      paymentMethod,
    };

    try {
      await addDoc(collection(db, "transactions"), transactionData);

      setTimeout(() => {
        window.print();
        setTimeout(() => {
          setQuantities({});
          setDevoteeName("");
          setDevoteeMobile("");
          setPaymentMethod("Cash");
          setPendingId(null);
        }, 500);
      }, 100);
    } catch (error) {
      console.error("Error saving transaction");
      setPendingId(null);
    }
  };

  const handleReprint = (transaction) => {
    setReprintData(transaction);
    setTimeout(() => {
      window.print();
      setReprintData(null);
    }, 1200);
  };

  const updateQuantity = (id, delta) =>
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] || 0) + delta),
    }));
  const calculateTotal = () =>
    sevalu.reduce(
      (acc, item) => acc + item.dhara * (quantities[item.id] || 0),
      0,
    );
  const selectedItems = sevalu.filter((item) => quantities[item.id] > 0);

  // --- FILTERING & STATS ---
  const filteredHistory = useMemo(() => {
    return history.filter((trans) => {
      const matchSearch =
        trans.items?.some((i) =>
          i.peru.toLowerCase().includes(searchTerm.toLowerCase()),
        ) ||
        trans.devoteeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trans.devoteeMobile?.includes(searchTerm);
      let matchTime = true;
      if (filterType === "date") matchTime = trans.rawDate === filterDate;
      else if (filterType === "month")
        matchTime = trans.rawDate.startsWith(filterMonth);
      else if (filterType === "year")
        matchTime = trans.rawDate.startsWith(filterYear);
      else if (filterType === "all") matchTime = true;
      return matchSearch && matchTime;
    });
  }, [history, searchTerm, filterType, filterDate, filterMonth, filterYear]);

  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    const todayTrans = history.filter((t) => t.rawDate === todayStr);
    return {
      todayTotal: todayTrans.reduce((a, b) => a + b.total, 0),
      todaySales: todayTrans.length,
      overallTotal: history.reduce((a, b) => a + b.total, 0),
      overallSales: history.length,
    };
  }, [history]);

  const exportCSV = () => {
    if (filteredHistory.length === 0) return;
    const headers = [
      "Sr No", // Changed Label
      "Date",
      "Name",
      "Mobile",
      "Mode",
      "Items",
      "Total",
    ];
    const rows = filteredHistory.map((t) => [
      dailyIdMap[t.id] || t.transactionId,
      t.dateString,
      t.devoteeName || "N/A",
      t.devoteeMobile || "N/A",
      t.paymentMethod || "Cash",
      t.items.map((i) => `${i.peru} x${i.qty}`).join(" | "),
      t.total,
    ]);
    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `Report_${new Date().toLocaleDateString()}.csv`,
    );
    link.click();
  };

  const activeReceiptItems = reprintData
    ? reprintData.items
    : selectedItems.map((i) => ({ ...i, qty: quantities[i.id] }));
  const activeTotal = reprintData ? reprintData.total : calculateTotal();
  const activePayment = reprintData ? reprintData.paymentMethod : paymentMethod;

  const todayStr = new Date().toISOString().split("T")[0];
  const previewDaily = history.filter((t) => t.rawDate === todayStr).length + 1;
  const currentPreviewId = previewDaily.toString();

  return (
    <div className="flex flex-col min-h-screen bg-stone-100 font-sans text-stone-900">
      <style>{`@media print { @page { size: auto; margin: 0mm; } body { background: white !important; margin: 0 !important; } .print-area { display: block !important; width: 100% !important; padding: 10mm !important; } .no-print { display: none !important; } }`}</style>
      <div className="print:hidden no-print">
        <Navbar />
      </div>

      <main className="flex-grow max-w-7xl w-full mx-auto p-4 md:p-8 mb-10">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 no-print print:hidden">
          <div className="flex bg-stone-200 p-1.5 rounded-2xl shadow-inner">
            <button
              onClick={() => setActiveTab("billing")}
              className={`px-6 py-2.5 rounded-xl font-bold transition-all ${
                activeTab === "billing"
                  ? "bg-white text-orange-600 shadow-sm"
                  : "text-stone-500"
              }`}
            >
              Billing
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`px-6 py-2.5 rounded-xl font-bold transition-all ${
                activeTab === "reports"
                  ? "bg-white text-orange-600 shadow-sm"
                  : "text-stone-500"
              }`}
            >
              Reports
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowSevaModal(true)}
              className="bg-stone-800 text-white px-5 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-stone-700 shadow-lg transition-all"
            >
              <Settings size={18} /> Manage Sevalu
            </button>
            <div className="bg-white px-6 py-3 rounded-2xl border shadow-sm flex items-center gap-3 font-bold">
              <Clock className="text-orange-600 animate-pulse" size={20} />{" "}
              {currentTime.toLocaleTimeString("en-IN")}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
          <div
            className={`${
              activeTab === "billing" ? "lg:col-span-8" : "lg:col-span-12"
            } space-y-8 no-print`}
          >
            {activeTab === "billing" ? (
              <section className="space-y-6">
                <h2 className="text-2xl font-serif font-bold text-orange-900 flex items-center gap-2 px-1">
                  <Plus
                    className="bg-orange-600 text-white rounded-full p-1"
                    size={24}
                  />{" "}
                  Select Sevalu
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sevalu.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white p-6 rounded-2xl border flex justify-between items-center hover:border-orange-300 shadow-sm transition-all"
                    >
                      <div className="max-w-[60%]">
                        <p className="font-bold text-lg text-stone-800">
                          {item.peru}
                        </p>
                        <p className="text-orange-600 font-black">
                          Rs. {item.dhara}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 bg-stone-50 p-2 rounded-xl border">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="p-1 text-orange-600"
                        >
                          <Minus size={18} />
                        </button>
                        <span className="font-bold text-xl min-w-[20px] text-center">
                          {quantities[item.id] || 0}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="p-1 text-orange-600"
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-orange-50 border-2 border-orange-100 p-8 rounded-[2.5rem] space-y-6 animate-in zoom-in duration-300">
                  <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2 mb-2">
                    <User size={20} /> Devotee Details (Optional)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <input
                      type="text"
                      placeholder="Devotee Name"
                      value={devoteeName}
                      onChange={(e) => setDevoteeName(e.target.value)}
                      className="w-full px-6 py-4 rounded-2xl border-none shadow-sm focus:ring-2 focus:ring-orange-500 font-bold bg-white outline-none"
                    />
                    <div className="space-y-1">
                      <input
                        type="tel"
                        placeholder="Mobile Number"
                        value={devoteeMobile}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (val.length <= 10) setDevoteeMobile(val);
                        }}
                        className={`w-full px-6 py-4 rounded-2xl border-2 shadow-sm outline-none font-bold bg-white transition-all ${
                          mobileError
                            ? "border-red-400"
                            : "border-transparent focus:ring-orange-500"
                        }`}
                      />
                      {mobileError && (
                        <p className="text-red-500 text-[10px] font-bold pl-2">
                          {mobileError}
                        </p>
                      )}
                    </div>
                    <div className="relative">
                      <CreditCard
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-400"
                        size={18}
                      />
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full pl-12 pr-6 py-4 rounded-2xl border-none shadow-sm focus:ring-2 focus:ring-orange-500 font-bold bg-white outline-none appearance-none cursor-pointer"
                      >
                        <option value="Cash">Cash Payment</option>
                        <option value="Online">Online Payment</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <div className="space-y-8 animate-in fade-in">
                {/* Stats Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                  <div className="bg-orange-600 p-6 rounded-[2.5rem] text-white shadow-xl flex flex-col justify-center">
                    <p className="text-[10px] uppercase font-bold text-orange-100 mb-1">
                      Today Total
                    </p>
                    <h3 className="text-3xl font-black">₹{stats.todayTotal}</h3>
                  </div>
                  <div className="bg-white p-6 rounded-[2.5rem] border shadow-sm flex flex-col justify-center text-center">
                    <p className="text-[10px] uppercase font-bold text-stone-400 mb-1">
                      Today Sales
                    </p>
                    <h3 className="text-3xl font-black text-stone-700">
                      {stats.todaySales}
                    </h3>
                  </div>
                  <div className="bg-stone-800 p-6 rounded-[2.5rem] text-white shadow-xl flex flex-col justify-center text-center">
                    <p className="text-[10px] uppercase font-bold text-stone-400 mb-1">
                      Overall Total
                    </p>
                    <h3 className="text-3xl font-black text-white">
                      ₹{stats.overallTotal}
                    </h3>
                  </div>
                  <div className="bg-white p-6 rounded-[2.5rem] border shadow-sm flex flex-col justify-center text-center">
                    <p className="text-[10px] uppercase font-bold text-stone-400 mb-1">
                      Overall Sales
                    </p>
                    <h3 className="text-3xl font-black text-stone-700">
                      {stats.overallSales}
                    </h3>
                  </div>
                </div>

                <section className="bg-white p-8 md:p-12 rounded-[3rem] border border-stone-200 shadow-sm space-y-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b pb-8">
                    <div className="flex items-center gap-4">
                      <h2 className="text-2xl font-bold text-stone-600 flex items-center gap-2">
                        <ListRestart className="text-orange-600" size={28} />{" "}
                        Records
                      </h2>
                      <button
                        onClick={exportCSV}
                        className="bg-stone-900 text-white px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 shadow-md hover:bg-stone-800 transition-all mr-2"
                      >
                        <Download size={16} /> DOWNLOAD REPORT
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                      <div className="flex bg-stone-100 p-1 rounded-2xl border">
                        {["date", "month", "year", "all"].map((t) => (
                          <button
                            key={t}
                            onClick={() => setFilterType(t)}
                            className={`px-4 py-2 text-xs font-bold uppercase rounded-xl transition-all ${
                              filterType === t
                                ? "bg-white text-orange-600 shadow-sm"
                                : "text-stone-400"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>

                      {filterType === "date" && (
                        <input
                          type="date"
                          value={filterDate}
                          onChange={(e) => setFilterDate(e.target.value)}
                          className="px-4 py-2 rounded-xl border border-stone-200 text-sm font-bold outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      )}
                      {filterType === "month" && (
                        <input
                          type="month"
                          value={filterMonth}
                          onChange={(e) => setFilterMonth(e.target.value)}
                          className="px-4 py-2 rounded-xl border border-stone-200 text-sm font-bold outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      )}
                      {filterType === "year" && (
                        <select
                          value={filterYear}
                          onChange={(e) => setFilterYear(e.target.value)}
                          className="px-4 py-2 rounded-xl border border-stone-200 text-sm font-bold outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                        >
                          {Array.from(
                            { length: 5 },
                            (_, i) => new Date().getFullYear() - i,
                          ).map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      )}

                      <div className="relative flex-grow md:flex-grow-0 min-w-[200px]">
                        <Search
                          className="absolute left-4 top-3 text-stone-300"
                          size={18}
                        />
                        <input
                          type="text"
                          placeholder="Search records..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-11 pr-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    ref={scrollRef}
                    className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar scroll-smooth"
                  >
                    {filteredHistory.map((trans) => (
                      <div
                        key={trans.id}
                        className="p-6 rounded-2xl border transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-orange-200 hover:shadow-md"
                      >
                        <div className="flex gap-4 items-center w-full sm:w-auto">
                          <div>
                            <div className="flex flex-wrap items-center gap-x-3 mb-1">
                              {/* UPDATED SR NUMBER DISPLAY IN RECORDS LIST */}
                              <p className="text-xs font-black text-stone-800 uppercase leading-none bg-stone-200 px-2 py-1 rounded-md">
                                Sr No:{" "}
                                {dailyIdMap[trans.id] || trans.transactionId}
                              </p>
                              <span
                                className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase ${
                                  trans.paymentMethod === "Cash"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-blue-100 text-blue-700"
                                }`}
                              >
                                {trans.paymentMethod || "Cash"}
                              </span>
                              {trans.devoteeName && (
                                <p className="text-xs font-black text-stone-700 flex items-center gap-1">
                                  <User size={12} className="text-orange-500" />{" "}
                                  {trans.devoteeName}
                                </p>
                              )}
                              {trans.devoteeMobile && (
                                <p className="text-xs font-black text-stone-500 flex items-center gap-1">
                                  <Phone size={12} className="text-stone-400" />{" "}
                                  {trans.devoteeMobile}
                                </p>
                              )}
                            </div>
                            <p className="text-xs font-bold text-orange-500">
                              {trans.dateString} • {trans.timeString}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {trans.items?.map((i, idx) => (
                                <span
                                  key={idx}
                                  className="bg-white border border-stone-100 text-[10px] font-bold px-2.5 py-1 rounded-lg uppercase shadow-sm"
                                >
                                  {i.peru} x{i.qty}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between w-full sm:w-auto sm:gap-6 border-t sm:border-0 pt-4 sm:pt-0">
                          <p className="text-xl font-black text-stone-800 tracking-tighter">
                            ₹{trans.total}
                          </p>
                          <button
                            onClick={() => handleReprint(trans)}
                            className="p-3 bg-stone-50 text-stone-400 hover:text-orange-600 rounded-xl transition-all shadow-sm"
                          >
                            <Printer size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      scrollRef.current.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      })
                    }
                    className="absolute bottom-6 right-8 p-3.5 bg-white text-stone-400 hover:bg-orange-600 hover:text-white rounded-full shadow-xl border transition-all active:scale-95"
                  >
                    <ArrowUp size={24} />
                  </button>
                </section>
              </div>
            )}
          </div>

          {/* Receipt Preview */}
          <div
            className={`lg:col-span-4 print-area ${
              reprintData
                ? "!block fixed inset-0 z-[200] bg-white !w-full h-full"
                : activeTab === "billing"
                  ? "block"
                  : "hidden"
            } print:fixed print:inset-0 print:z-[200] print:bg-white print:block print:w-full`}
          >
            <div
              className={`bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-orange-100 sticky top-24 print:border-0 print:shadow-none print:w-full ${
                !reprintData && selectedItems.length === 0
                  ? "opacity-10 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <div className="bg-white p-8 border-b-[6px] border-orange-500">
                <img
                  src="/icon.png"
                  alt="Logo"
                  className="w-20 h-20 object-contain mx-auto mb-4"
                />
                <h1 className="text-xl font-bold uppercase leading-tight text-stone-700 text-center">
                  Sri Bramhasutra Marakatha Shivalingam Devasthanam{" "}
                </h1>
                <p className="text-[12px] text-stone-900 font-bold tracking-widest mt-2 uppercase text-center">
                  Chandippa, Shankarpalli, Telangana, India-501203
                </p>

                {/* Date moved here, small and centered */}
                <p className="text-[12px] text-stone-900 font-bold tracking-widest mt-1 text-center uppercase">
                  Date:{" "}
                  {reprintData
                    ? `${reprintData.dateString} ${reprintData.timeString}`
                    : `${new Date().toLocaleDateString(
                        "en-IN",
                      )} ${new Date().toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}`}
                </p>

                {/* UPDATED HEADER LAYOUT: Vertically Centered */}
                {/* UPDATED HEADER LAYOUT: Improved for 3+ digit Sr Numbers */}
                <div className="flex justify-between items-end mt-6 pt-4 border-t-2 border-stone-800 gap-2">
                  <h2 className="text-3xl font-black text-stone-900 uppercase tracking-tighter leading-none">
                    Receipt
                  </h2>
                  <div className="text-right flex-shrink-0">
                    <div className="flex flex-col items-end">
                      <span className="text-[12px] font-bold text-stone-900 uppercase leading-none mb-1">
                        Sr No
                      </span>
                      <p className="text-3xl md:text-4xl font-black text-black leading-none tabular-nums">
                        {reprintData
                          ? dailyIdMap[reprintData.id] ||
                            reprintData.transactionId
                          : pendingId || currentPreviewId}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 space-y-6">
                {(reprintData?.devoteeName || devoteeName || activePayment) && (
                  <div className="border-b border-dashed border-stone-200 pb-4 text-sm font-bold text-stone-600 space-y-1">
                    {(reprintData?.devoteeName || devoteeName) && (
                      <p className="flex justify-between">
                        Name:{" "}
                        <span>
                          {reprintData ? reprintData.devoteeName : devoteeName}
                        </span>
                      </p>
                    )}
                    {(reprintData?.devoteeMobile || devoteeMobile) && (
                      <p className="flex justify-between">
                        Mobile:{" "}
                        <span>
                          {reprintData
                            ? reprintData.devoteeMobile
                            : devoteeMobile}
                        </span>
                      </p>
                    )}
                    <p className="flex justify-between text-stone-900">
                      Payment Method: <span>{activePayment}</span>
                    </p>
                  </div>
                )}
                <table className="w-full text-left text-sm font-medium">
                  <thead>
                    <tr className="text-stone-900 border-b uppercase font-bold">
                      <th className="pb-3">Item</th>
                      <th className="pb-3 text-center">Qty</th>
                      <th className="pb-3 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y text-sm divide-stone-50">
                    {activeReceiptItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-4 font-bold text-stone-800">
                          {item.peru}
                        </td>
                        <td className="py-4 text-center font-bold">
                          x{item.qty}
                        </td>
                        <td className="py-4 text-right font-black text-orange-600">
                          ₹{item.dhara * item.qty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="border-t-4 border-double border-stone-200 pt-6">
                  <div className="flex justify-between items-center mb-8 font-black text-stone-900 text-4xl">
                    <span className="text-sm uppercase text-stone-900 font-bold">
                      Total
                    </span>
                    ₹{activeTotal}
                  </div>
                  {!reprintData && activeTab === "billing" && (
                    <button
                      onClick={handlePrint}
                      disabled={selectedItems.length === 0 || !!mobileError}
                      className="w-full py-5 mt-6 rounded-[2rem] font-black bg-orange-600 text-white shadow-xl flex items-center justify-center gap-3 no-print active:scale-95 transition-all"
                    >
                      GENERATE RECEIPT
                    </button>
                  )}
                </div>
              </div>
              <div className="bg-stone-50 p-6 text-center border-t border-dashed">
                <p className="text-[12px] font-bold text-black-900 italic tracking-[0.1em] text-center">
                  Powered by MNR Technologies Global Pvt Ltd
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* MANAGE SEVALU MODAL */}
      {showSevaModal && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 no-print">
          <div
            className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"
            onClick={() => {
              setShowSevaModal(false);
              setEditingSevaId(null);
              setNewSeva({ peru: "", dhara: "" });
            }}
          ></div>
          <div className="bg-white w-full max-w-xl rounded-[3rem] p-10 shadow-2xl relative z-10 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-bold">
                {editingSevaId ? "Edit Item" : "Manage Seva"}
              </h3>
              <button onClick={() => setShowSevaModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveSeva} className="flex gap-4 mb-8">
              <input
                type="text"
                placeholder="Seva Name"
                value={newSeva.peru}
                onChange={(e) =>
                  setNewSeva({ ...newSeva, peru: e.target.value })
                }
                className="flex-1 px-5 py-4 rounded-2xl bg-stone-100 outline-none font-bold"
              />
              <input
                type="number"
                placeholder="Price"
                value={newSeva.dhara}
                onChange={(e) =>
                  setNewSeva({ ...newSeva, dhara: e.target.value })
                }
                className="w-24 px-5 py-4 rounded-2xl bg-stone-100 outline-none font-bold"
              />
              <button
                type="submit"
                className="bg-orange-600 text-white p-4 rounded-2xl shadow-lg active:scale-95 transition-all"
              >
                <Save size={20} />
              </button>
            </form>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {sevalu.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl border"
                >
                  <div>
                    <p className="font-bold">{item.peru}</p>
                    <p className="text-orange-600 text-xs font-black">
                      Rs. {item.dhara}
                    </p>
                  </div>
                  <button
                    onClick={() => startEditSeva(item)}
                    className="p-2 text-stone-400 hover:text-orange-600 transition-all"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="print:hidden no-print">
        <Footer />
      </div>
    </div>
  );
};

export default Dashboard;
